import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Howl } from 'howler'

export default function AudioControls({ audioSrc, songTitle = "Child of God", artist = "BLXT" }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isLoaded, setIsLoaded] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [showPrompt, setShowPrompt] = useState(true)
  const soundRef = useRef(null)
  const progressInterval = useRef(null)

  useEffect(() => {
    if (audioSrc) {
      soundRef.current = new Howl({
        src: [audioSrc],
        html5: true,
        preload: true,
        loop: true,
        volume: 0.7,
        onload: () => {
          console.log('Audio loaded successfully')
          setIsLoaded(true)
          setHasError(false)
        },
        onloaderror: (id, error) => {
          console.error('Audio load error:', error)
          setHasError(true)
        },
        onplayerror: (id, error) => {
          console.error('Audio play error:', error)
          // Try to unlock and play again
          soundRef.current.once('unlock', () => {
            soundRef.current.play()
          })
        },
        onend: () => {
          setProgress(0)
        }
      })
    }

    return () => {
      if (soundRef.current) {
        soundRef.current.unload()
      }
      if (progressInterval.current) {
        clearInterval(progressInterval.current)
      }
    }
  }, [audioSrc])

  useEffect(() => {
    if (isPlaying) {
      progressInterval.current = setInterval(() => {
        if (soundRef.current) {
          const seek = soundRef.current.seek()
          const duration = soundRef.current.duration()
          setProgress((seek / duration) * 100)
        }
      }, 100)
    } else if (progressInterval.current) {
      clearInterval(progressInterval.current)
    }

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current)
      }
    }
  }, [isPlaying])

  const togglePlay = () => {
    if (!soundRef.current) return

    if (isPlaying) {
      soundRef.current.pause()
    } else {
      soundRef.current.play()
    }
    setIsPlaying(!isPlaying)
    setShowPrompt(false)
  }

  const toggleMute = () => {
    if (!soundRef.current) return
    soundRef.current.mute(!isMuted)
    setIsMuted(!isMuted)
  }

  // If no audio source, don't render
  if (!audioSrc) {
    return null
  }

  return (
    <>
      {/* Audio Prompt */}
      <AnimatePresence>
        {showPrompt && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-dark-primary/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="text-center p-8 glass-card max-w-md"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
            >
              <span className="text-6xl block mb-4">🎵</span>
              <h3 className="text-2xl font-bold text-white mb-2">
                Expérience Audio
              </h3>
              <p className="text-light-pink-100/70 mb-4">
                Cette expérience est accompagnée d'une bande sonore.
              </p>
              <div className="mb-6 py-3 px-4 bg-primary-pink/10 rounded-lg border border-primary-pink/20">
                <p className="text-primary-pink font-semibold">{songTitle}</p>
                <p className="text-light-pink-100/60 text-sm">{artist}</p>
              </div>
              {hasError && (
                <p className="text-red-400 text-sm mb-4">
                  Erreur de chargement audio. Vérifie que le fichier existe.
                </p>
              )}
              <div className="flex gap-4 justify-center">
                <motion.button
                  onClick={togglePlay}
                  className={`px-6 py-3 font-bold rounded-full ${
                    hasError
                      ? 'bg-gray-500 cursor-not-allowed'
                      : 'bg-primary-pink text-dark-primary'
                  }`}
                  whileHover={!hasError ? { scale: 1.05 } : {}}
                  whileTap={!hasError ? { scale: 0.95 } : {}}
                  disabled={hasError}
                >
                  {hasError ? '❌ Audio indisponible' : '🔊 Avec Audio'}
                </motion.button>
                <motion.button
                  onClick={() => setShowPrompt(false)}
                  className="px-6 py-3 bg-white/10 text-white font-bold rounded-full"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  🔇 Sans Audio
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Controls */}
      <motion.div
        className="fixed bottom-6 right-6 z-40 flex items-center gap-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        {/* Song Info - visible when playing */}
        <AnimatePresence>
          {isPlaying && (
            <motion.div
              className="hidden sm:flex flex-col items-end mr-2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <span className="text-xs text-primary-pink font-medium">{songTitle}</span>
              <span className="text-xs text-light-pink-100/50">{artist}</span>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Progress Ring */}
        <div className="relative w-14 h-14">
          <svg className="w-full h-full -rotate-90">
            <circle
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="4"
            />
            <circle
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke="#E851B2"
              strokeWidth="4"
              strokeDasharray={`${progress * 1.51} 151`}
              strokeLinecap="round"
            />
          </svg>

          {/* Play/Pause Button */}
          <motion.button
            className="absolute inset-0 flex items-center justify-center bg-dark-primary/80 backdrop-blur-sm rounded-full"
            onClick={togglePlay}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            disabled={!isLoaded}
          >
            <span className="text-xl">
              {isPlaying ? '⏸️' : '▶️'}
            </span>
          </motion.button>
        </div>

        {/* Mute Button */}
        <motion.button
          className="w-10 h-10 flex items-center justify-center bg-dark-primary/80 backdrop-blur-sm rounded-full border border-white/10"
          onClick={toggleMute}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <span className="text-lg">
            {isMuted ? '🔇' : '🔊'}
          </span>
        </motion.button>
      </motion.div>
    </>
  )
}
